import { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';

const TimelineContext = createContext(null);

function formatSkipDuration(ms) {
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainMinutes = minutes % 60;
  return remainMinutes > 0 ? `${hours}h ${remainMinutes}m` : `${hours}h`;
}

export function TimelineProvider({
  bounds, recordings = [], events = [],
  skipEmptyTime = false, highlightReelMode = false, highlightReelLag = 5,
  children,
}) {
  const [currentTime, setCurrentTime] = useState(bounds.start);
  const [isPlaying, setIsPlaying] = useState(false);
  const [window, setWindow] = useState(null); // { start, end } or null = full duration
  const timeRef = useRef(bounds.start);
  const intervalRef = useRef(null);
  // Track which event we last skipped to, so we don't re-toast the same one
  const lastSkippedEventIdx = useRef(-1);

  // Pre-compute sorted recording intervals for gap skipping
  const sortedRecordings = useRef([]);
  useEffect(() => {
    if (recordings.length > 0) {
      sortedRecordings.current = [...recordings]
        .sort((a, b) => a.startTime - b.startTime)
        .map((r) => ({ start: r.startTime, end: r.endTime }));
    }
  }, [recordings]);

  // Pre-compute sorted events for highlight reel
  const sortedEvents = useMemo(() =>
    [...events].sort((a, b) => a.startTime - b.startTime),
    [events]
  );

  const findNextRecordingStart = useCallback((time) => {
    const next = sortedRecordings.current.find((rec) => rec.start > time);
    return next ? next.start : null;
  }, []);

  const isInAnyRecording = useCallback((time) => {
    return sortedRecordings.current.some((rec) => time >= rec.start && time <= rec.end);
  }, []);

  const isInAnyEvent = useCallback((time) => {
    return sortedEvents.some((evt) => time >= evt.startTime && time <= evt.endTime);
  }, [sortedEvents]);

  const findNextEvent = useCallback((time) => {
    const idx = sortedEvents.findIndex((evt) => evt.startTime > time);
    return idx >= 0 ? { event: sortedEvents[idx], index: idx } : null;
  }, [sortedEvents]);

  const stopInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const seek = useCallback((time) => {
    const clamped = Math.max(bounds.start, Math.min(bounds.end, time));
    timeRef.current = clamped;
    setCurrentTime(clamped);
  }, [bounds]);

  const play = useCallback(() => {
    lastSkippedEventIdx.current = -1;
    setIsPlaying(true);
  }, []);

  const pause = useCallback(() => {
    stopInterval();
    setCurrentTime(timeRef.current);
    setIsPlaying(false);
  }, [stopInterval]);

  const clearWindow = useCallback(() => setWindow(null), []);

  // Drive playback with setInterval for reliable, consistent state updates
  useEffect(() => {
    if (!isPlaying) {
      stopInterval();
      return undefined;
    }

    let lastTick = performance.now();
    intervalRef.current = setInterval(() => {
      const now = performance.now();
      const delta = Math.min(now - lastTick, 200);
      lastTick = now;

      const end = window?.end ?? bounds.end;
      let next = timeRef.current + delta;

      // Highlight reel mode: skip to next event when outside any event
      if (highlightReelMode && next < end && !isInAnyEvent(next)) {
        const nextEvt = findNextEvent(next);
        if (nextEvt && nextEvt.event.startTime <= end) {
          // Jump to (event start - lag), clamped to timeline start
          const lagMs = highlightReelLag * 1000;
          const jumpTarget = Math.max(bounds.start, nextEvt.event.startTime - lagMs);
          // Only skip if the target is actually ahead of us (avoid backward jumps from lag)
          if (jumpTarget > next) {
            // Only toast if this is a new event we haven't announced
            if (nextEvt.index !== lastSkippedEventIdx.current) {
              lastSkippedEventIdx.current = nextEvt.index;
              const skippedMs = jumpTarget - next;
              if (skippedMs > 1000) {
                toast.info(
                  `Skipped to event: ${nextEvt.event.label || 'Rule violation'}`,
                  { autoClose: 2000, hideProgressBar: true }
                );
              }
            }
            next = jumpTarget;
          }
        } else {
          // No more events — stop playback
          timeRef.current = end;
          setCurrentTime(end);
          setIsPlaying(false);
          stopInterval();
          toast.info('Highlight reel complete', { autoClose: 2000, hideProgressBar: true });
          return;
        }
      }

      // Skip empty time: if we've left all recordings, jump to the next one
      if (skipEmptyTime && !highlightReelMode && next < end && !isInAnyRecording(next)) {
        const nextStart = findNextRecordingStart(next);
        if (nextStart && nextStart <= end) {
          const skippedMs = nextStart - next;
          toast.info(`Skipped ${formatSkipDuration(skippedMs)} gap`, { autoClose: 2000, hideProgressBar: true });
          next = nextStart;
        }
      }

      if (next >= end) {
        timeRef.current = end;
        setCurrentTime(end);
        setIsPlaying(false);
        stopInterval();
      } else {
        timeRef.current = next;
        setCurrentTime(next);
      }
    }, 50);

    return () => stopInterval();
  }, [isPlaying, bounds, window, stopInterval, skipEmptyTime, highlightReelMode,
      isInAnyRecording, findNextRecordingStart, isInAnyEvent, findNextEvent, highlightReelLag]);

  return (
    <TimelineContext.Provider value={{ currentTime, isPlaying, seek, play, pause, bounds, window, setWindow, clearWindow }}>
      {children}
    </TimelineContext.Provider>
  );
}

export function useTimeline() {
  const ctx = useContext(TimelineContext);
  if (!ctx) throw new Error('useTimeline must be used within TimelineProvider');
  return ctx;
}

export default TimelineContext;
